import React from 'react';
import { Button, Checkbox, Input, Select } from 'antd';
import {
  DEFAULT_MAP_PROVIDER_SETTINGS,
  MAP_PROVIDER_CATALOG,
  normalizeMapProviderSettings,
  listBasemapFamilies,
  isFamilyEnabled,
  setFamilyEnabled,
  getBasemapFamily
} from '../../../utils/mapProviderSettings';

const DEFAULT_STYLE_MODE_OPTIONS = [
  { value: 'streets', label: '二维地图' },
  { value: 'satellite', label: '卫星地图' },
  { value: 'hybrid', label: '混合地图（影像+路网+山峰）' }
];

export function buildMapProviderTab(context) {
  const { shouldShowPage, handleSaveSystemSettings, updateSystemSettings, systemSettings } = context;

  if (
    !shouldShowPage?.('底图源设置')
    && !shouldShowPage?.('地图设置')
    && !shouldShowPage?.('系统设置')
  ) {
    return null;
  }

  const mapSettings = normalizeMapProviderSettings(systemSettings.mapProviderSettings);
  const providers = MAP_PROVIDER_CATALOG;
  const families = listBasemapFamilies();
  const updateMapSettings = (patch) => updateSystemSettings((settings) => ({
    ...settings,
    mapProviderSettings: normalizeMapProviderSettings({
      ...(settings.mapProviderSettings || {}),
      ...patch
    })
  }));

  const defaultFamily = getBasemapFamily(mapSettings.defaultBasemapId);

  return {
    key: 'map_provider_settings',
    label: '底图源设置',
    children: (
      <div>
        <h3>底图源设置</h3>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={handleSaveSystemSettings}>保存设置</Button>
          <Button style={{ marginLeft: 8 }} onClick={() => updateMapSettings(DEFAULT_MAP_PROVIDER_SETTINGS)}>重置默认</Button>
        </div>
        <p style={{ color: '#8c5b00' }}>
          配置的 Key 会下发至指挥页浏览器以拉取瓦片，请在图商后台设置域名白名单。
          指挥页<strong>每次刷新</strong>都会恢复为下方默认图源与模式（不记住上次选择），避免无 VPN 时卡在谷歌等外网图源打不开。
          指挥页顶栏用「二维 / 卫星 / 混合」切换同一图源的样式，底图列表不再重复列出高德二维、高德卫星等。
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
          <div>
            <div>天地图 Key</div>
            <Input.Password value={mapSettings.keys.tianditu} onChange={(e) => updateMapSettings({ keys: { ...mapSettings.keys, tianditu: e.target.value } })} />
          </div>
          <div>
            <div>高德 Key（可选）</div>
            <Input.Password value={mapSettings.keys.amap} onChange={(e) => updateMapSettings({ keys: { ...mapSettings.keys, amap: e.target.value } })} />
          </div>
          <div>
            <div>谷歌 Key（可选）</div>
            <Input.Password value={mapSettings.keys.google} onChange={(e) => updateMapSettings({ keys: { ...mapSettings.keys, google: e.target.value } })} placeholder="栅格瓦片通常可不填" />
          </div>
          <div>
            <div>默认图源（刷新后生效）</div>
            <Select
              style={{ width: '100%' }}
              value={defaultFamily}
              onChange={(family) => {
                const group = families.find((f) => f.family === family);
                const seedId = group?.seedId || mapSettings.defaultBasemapId;
                updateMapSettings({
                  defaultBasemapId: seedId,
                  enabled: setFamilyEnabled(mapSettings.enabled, family, true)
                });
              }}
              options={families.map((group) => ({
                value: group.family,
                label: isFamilyEnabled(mapSettings, group.family)
                  ? group.label
                  : `${group.label}（未启用）`
              }))}
            />
          </div>
          <div>
            <div>默认模式（刷新后生效）</div>
            <Select
              style={{ width: '100%' }}
              value={['streets', 'satellite', 'hybrid'].includes(mapSettings.defaultStyleMode)
                ? mapSettings.defaultStyleMode
                : 'satellite'}
              onChange={(defaultStyleMode) => updateMapSettings({ defaultStyleMode })}
              options={DEFAULT_STYLE_MODE_OPTIONS}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
              二维=路网，卫星=纯影像，混合=影像叠加道路并开立体山峰。若该图源没有对应样式，会自动落到它能显示的第一种。
            </div>
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <h4>底图启用（按图源，指挥页列表只显示这些名字）</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
            {families.map((group) => (
              <Checkbox
                key={group.family}
                checked={isFamilyEnabled(mapSettings, group.family)}
                onChange={(e) => updateMapSettings({
                  enabled: setFamilyEnabled(mapSettings.enabled, group.family, e.target.checked)
                })}
              >
                {group.label}
              </Checkbox>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <h4>注记叠加启用（多选）</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
            {providers.filter((item) => item.role === 'overlay').map((item) => {
              const checked = Object.prototype.hasOwnProperty.call(mapSettings.enabled, item.id)
                ? !!mapSettings.enabled[item.id]
                : item.defaultEnabled !== false;
              return (
                <Checkbox
                  key={item.id}
                  checked={checked}
                  onChange={(e) => updateMapSettings({
                    enabled: { ...mapSettings.enabled, [item.id]: e.target.checked }
                  })}
                >
                  {item.label}
                </Checkbox>
              );
            })}
          </div>
        </div>
        <h4 style={{ marginTop: 20 }}>自定义影像瓦片地址</h4>
        {['xingtu-img', 'changguang-img', 'jilin-img'].map((id) => (
          <div key={id} style={{ marginBottom: 10 }}>
            <div>{providers.find((item) => item.id === id)?.label}</div>
            <Input value={mapSettings.customTiles[id] || ''} placeholder="https://example.com/{z}/{x}/{y}.png" onChange={(e) => updateMapSettings({ customTiles: { ...mapSettings.customTiles, [id]: e.target.value } })} />
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#666' }}>坐标系</span>
              <Select
                style={{ width: 280 }}
                value={(mapSettings.customTileDatums && mapSettings.customTileDatums[id]) || ''}
                onChange={(v) => updateMapSettings({ customTileDatums: { ...(mapSettings.customTileDatums || {}), [id]: v } })}
                options={[
                  { value: '', label: '默认（WGS84，与原来一致）' },
                  { value: 'WGS84', label: 'WGS84（天地图/标准 WMTS）' },
                  { value: 'GCJ02', label: 'GCJ-02（国测局，高德/谷歌国内源）' }
                ]}
              />
            </div>
          </div>
        ))}
      </div>
    )
  };
}
